import { MonitorOff, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";

import { useTeamStatus, type TeamMember } from "@/api/queries";
import { ActivityBar } from "@/components/common/ActivityBar";
import { EmptyState } from "@/components/common/EmptyState";
import { PageHeader } from "@/components/common/PageHeader";
import { UserAvatar } from "@/components/common/UserAvatar";
import { VacationBadge } from "@/components/common/VacationBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDuration, formatShortDateTime, formatSince, formatTime } from "@/lib/format";

import { ActivityTabs } from "./ActivityTabs";
import { TeamSelect } from "./TeamSelect";

/** Who is tracking right now, with their project and latest screenshot. */
export function NowPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? "ru";
  const [params, setParams] = useSearchParams();
  const teamId = params.get("team") ?? undefined;
  const status = useTeamStatus(teamId);
  const items = status.data?.items ?? [];
  const live = items.filter((m) => m.state !== "offline");
  const offline = items.filter((m) => m.state === "offline");
  const idle = live.filter((m) => m.state === "idle").length;

  return (
    <>
      <PageHeader
        title={t("nav.activity")}
        actions={
          <TeamSelect
            value={teamId}
            onChange={(team) => setParams(team ? { team } : {}, { replace: true })}
          />
        }
      />
      <ActivityTabs />
      {status.isError ? (
        <Card>
          <EmptyState
            icon={Users}
            title={t("common.error")}
            action={
              <Button variant="secondary" onClick={() => void status.refetch()}>
                {t("common.retry")}
              </Button>
            }
          />
        </Card>
      ) : status.isPending ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-72 w-full" />
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          <p className="text-muted" aria-live="polite">
            {t("now.summary", { working: live.length - idle, idle, offline: offline.length })} ·{" "}
            {t("now.subtitle")}
          </p>
          {live.length === 0 ? (
            <Card>
              <EmptyState
                icon={MonitorOff}
                title={t("now.emptyTitle")}
                description={t("now.emptyDescription")}
              />
            </Card>
          ) : (
            <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
              {live.map((m) => (
                <LiveCard key={m.user.id} member={m} lang={lang} />
              ))}
            </ul>
          )}
          {offline.length > 0 && (
            <section className="flex flex-col gap-3">
              <h2 className="font-semibold">{t("now.offlineTitle")}</h2>
              <Card className="overflow-hidden">
                <ul>
                  {offline.map((m, i) => (
                    <li key={m.user.id} className={i > 0 ? "border-t" : undefined}>
                      <Link
                        to={`/activity/${m.user.id}`}
                        className="flex items-center gap-3 px-5 py-2.5 hover:bg-surface-muted"
                      >
                        <UserAvatar name={m.user.full_name} className="size-7" />
                        <span className="flex min-w-0 flex-1 items-center gap-2">
                          <span className="truncate font-medium">{m.user.full_name}</span>
                          <VacationBadge vacation={m.user.vacation} />
                        </span>
                        <span className="hidden text-xs text-muted sm:inline">
                          {m.last_activity_at
                            ? t("now.lastActivity", {
                                time: formatShortDateTime(m.last_activity_at, lang),
                              })
                            : t("now.noActivity")}
                        </span>
                        <span className="w-20 text-right text-xs tabular-nums">
                          {formatDuration(m.today_seconds, lang)}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </Card>
            </section>
          )}
        </div>
      )}
    </>
  );
}

function LiveCard({ member: m, lang }: { member: TeamMember; lang: string }) {
  const { t } = useTranslation();
  const href = `/activity/${m.user.id}`;
  return (
    <li>
      <Card className="flex h-full flex-col gap-3 p-3">
        <Link
          to={href}
          className="block overflow-hidden rounded-md border"
          aria-label={t("dashboard.openTimeline")}
        >
          {m.last_screenshot ? (
            <img
              src={m.last_screenshot.thumb_url}
              alt={t("timeline.screenshotAlt", { time: formatTime(m.last_screenshot.taken_at, lang) })}
              className="aspect-[16/10] w-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="flex aspect-[16/10] items-center justify-center bg-surface-muted text-xs text-muted">
              {t("now.noScreenshot")}
            </div>
          )}
        </Link>
        <div className="flex items-center gap-2.5">
          <UserAvatar name={m.user.full_name} />
          <div className="min-w-0 flex-1">
            <Link to={href} className="block truncate font-medium hover:underline">
              {m.user.full_name}
            </Link>
            <VacationBadge vacation={m.user.vacation} />
            {m.last_screenshot && (
              <span className="block text-xs text-muted tabular-nums">
                {formatTime(m.last_screenshot.taken_at, lang)}
              </span>
            )}
          </div>
          <Badge variant={m.state === "idle" ? "warning" : "success"}>{t(`state.${m.state}`)}</Badge>
        </div>
        {m.current && (
          <div className="flex flex-col gap-0.5">
            <span className="flex items-center gap-1.5 font-medium">
              <span
                className="size-2 shrink-0 rounded-full"
                style={{ backgroundColor: m.current.project.color }}
                aria-hidden="true"
              />
              <span className="truncate">{m.current.project.name}</span>
            </span>
            <span className="truncate pl-3.5 text-xs text-muted">
              {m.current.task_title ? `${m.current.task_title} · ` : ""}
              {t("now.since", { time: formatSince(m.current.since, lang) })}
            </span>
          </div>
        )}
        <div className="mt-auto flex flex-col gap-1.5 border-t pt-2.5">
          <span className="text-xs text-muted">
            {t("now.today", { time: formatDuration(m.today_seconds, lang) })}
          </span>
          <ActivityBar percent={m.today_activity_percent} />
        </div>
      </Card>
    </li>
  );
}
